import type { ZodType } from 'zod';
import { getAccessToken, markUnauthenticated } from '../auth/oktaAuthProvider';

export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function parseJsonSafe(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

async function mergeAuthHeaders(init?: RequestInit): Promise<Headers> {
  const headers = new Headers(init?.headers);
  const token = await getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

/** GET/POST request that attaches Bearer auth (uploads, downloads, multipart). Clears session on 401. */
export async function authorizedFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = await mergeAuthHeaders(init);
  const res = await fetch(path, { ...init, headers });
  if (res.status === 401) {
    markUnauthenticated();
  }
  return res;
}

function parseDownloadFilename(contentDisposition: string | null): string | undefined {
  if (!contentDisposition) return undefined;

  const utf8 = /filename\*=(?:UTF-8''|utf-8'')([^;]+)/i.exec(contentDisposition);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^["']|["']$/g, ''));
    } catch {
      /* fall through */
    }
  }

  const quoted = /filename="([^"]*)"/i.exec(contentDisposition);
  if (quoted) return quoted[1];

  const plain = /filename=([^;\n]+)/i.exec(contentDisposition);
  if (plain?.[1]) return plain[1].trim().replace(/^["']|["']$/g, '');

  return undefined;
}

/**
 * Download a file from a GET API route with Bearer auth (replaces cookie-based navigation).
 */
export async function downloadAuthorized(path: string, suggestedFilename?: string): Promise<void> {
  const res = await authorizedFetch(path);
  if (res.status === 401) {
    throw new ApiError('Not authenticated', 401);
  }
  if (!res.ok) {
    const text = await res.text();
    throw new ApiError(text || `Download failed (${res.status})`, res.status);
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition');
  const filename =
    suggestedFilename ?? parseDownloadFilename(cd) ?? path.split('/').pop()?.split('?')[0] ?? 'download';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function apiFetch<T>(
  path: string,
  schema: ZodType<T>,
  init?: RequestInit
): Promise<T> {
  const headers = await mergeAuthHeaders(init);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...init,
    headers,
  });

  const text = await res.text();
  const json = text ? parseJsonSafe(text) : null;

  if (res.status === 401) {
    markUnauthenticated();
    throw new ApiError('Not authenticated', 401);
  }

  if (!res.ok) {
    const msg =
      json && typeof json === 'object' && json !== null && 'error' in json
        ? String((json as { error?: unknown }).error)
        : res.statusText;
    throw new ApiError(msg || `Request failed (${res.status})`, res.status);
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(parsed.error.message || 'Invalid response', res.status, 'ZOD');
  }
  return parsed.data;
}

export async function apiFetchEmpty(path: string, init?: RequestInit): Promise<void> {
  const headers = await mergeAuthHeaders(init);
  if (init?.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(path, {
    ...init,
    headers,
  });
  if (res.status === 401) {
    markUnauthenticated();
    throw new ApiError('Not authenticated', 401);
  }
  if (!res.ok) {
    const text = await res.text();
    const json = text ? parseJsonSafe(text) : null;
    const msg =
      json && typeof json === 'object' && json !== null && 'error' in json
        ? String((json as { error?: unknown }).error)
        : res.statusText;
    throw new ApiError(msg || `Request failed (${res.status})`, res.status);
  }
}
